'use client';

import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import {
  GOOGLE_LOGIN_ERROR,
  GOOGLE_LOGIN_ERROR_PARAM_PREFIX,
  type EmailSignInResponse,
} from '@mentorivo/shared';
import { toLocale } from '@mentorivo/shared/locales';

import {
  AuthBrand,
  AuthDivider,
  AuthField,
  FieldError,
  FormAlert,
  GoogleButton,
  SubmitButton,
  fieldV,
  legalChunks,
} from '@/components/auth-parts';
import { EmailCodeStep } from '@/components/email-code-step';
import { ApiError } from '@/lib/api';
import { afterSignInPath, returnPathFromLocation } from '@/lib/auth-return';
import { Link, useRouter } from '@/i18n/navigation';
import { dur, ease, sceneContainer, sceneItem, stagger } from '@/lib/motion';
import { startEmailSignIn } from '@/lib/session';
import { siteLinks } from '@/lib/site-links';

/**
 * Giriş formu — bölünmüş tuvalin beyaz yarısı.
 *
 * ── Parolasız (10 Eki 2026, kullanıcı kararı) ─────────────────────────────
 *
 * Tek alan: e-posta. Adrese bir posta gidiyor — altı haneli kod + giriş
 * bağlantısı (Claude / ChatGPT'deki gibi). Ekran iki adım:
 *
 *   1. adres     Google ile devam et · ya da adres → "E-posta ile devam et"
 *   2. kod       `EmailCodeStep` — kod girilince oturum açılıyor
 *
 * Giriş ile kayıt AYNI uç: adresin hesabı yoksa kod doğrulanınca açılıyor ve
 * kişi kurulum sihirbazına gidiyor. Bu yüzden ekran "böyle bir hesap yok"
 * DEMİYOR — diyemez de: sunucu her adrese aynı cevabı veriyor (kimin kayıtlı
 * olduğu bu formdan öğrenilemesin). Kayıt ekranının tek farkı ad sorması.
 *
 * Eski parola alanı, "şifremi unuttum" ve "beni hatırla" gitti. Sonuncusu
 * zaten hiçbir şeye bağlı değildi (değeri sunucuya gönderilmiyordu).
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Açılışta parçalar yukarıdan aşağı formun okunma sırasıyla odaklanıyor
 * (`sceneItem`: opaklık + 12px + blur) — kelime işareti, başlık, Google,
 * ayraç, alan, buton. Adres gönderilince adım YERİNDE değişiyor (`mode="wait"`):
 * eski panel tamamen çıkmadan yenisi girmiyor, ikisi aynı anda görünseydi
 * hangisinin geçerli olduğu belirsizleşirdi. Sarmalayıcıda `layout="position"`:
 * panelin boyu değişince alttaki yasal satır ZIPLAMIYOR, kayıyor.
 */

/** Kabaca "@ var, nokta var, boşluk yok". Asıl doğrulama sunucuda. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Adım paneli — çocukları `sceneItem` ile sırayla giriyor, çıkışta topluca. */
const stepV: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: dur.instant, staggerChildren: stagger.base, delayChildren: 0.04 },
  },
  exit: { opacity: 0, y: -8, filter: 'blur(4px)', transition: { duration: dur.instant, ease: ease.in } },
};

type Sent = { email: string; resendInSeconds: number };

export function LoginForm() {
  const t = useTranslations('login');
  const uid = useId();
  const router = useRouter();
  /* Hesap yoksa açılacak hesabın dili ve postanın dili — kişinin o an
     baktığı sayfanın dili (13 dil). Kayıtlı hesapta posta hesabın dilinde. */
  const locale = useLocale();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<Sent | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  /* İlk denemeden ÖNCE alan uyarısı basmak, kullanıcı daha yazarken
     "geçersiz" demek olur. Denemeden sonra anlık doğrulama açılıyor. */
  const [tried, setTried] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  /**
   * "Neden buradayım" satırı — oturum kapandığı için gelen kullanıcıya.
   *
   * Çıkış kapısı (`/api/session/end`) adrese `?session=expired|idle` yazıyor.
   * Bu satır olmadan kullanıcı, çalışırken birden giriş ekranında buluyordu
   * kendini ve tek makul yorumu "bir şey bozuldu" oluyordu.
   *
   * `useState` başlatıcısı bir KEZ çalışıyor: ilk gönderimde gerçek hata
   * onun yerini alıyor.
   */
  const [notice] = useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('session');
    if (reason === 'idle') return t('sessionIdle');
    if (reason === 'expired') return t('sessionExpired');

    /* Google dönüşünün hatası (`/login?error=google_<kod>`, API yazıyor).
       Bilinmeyen kod `failed` sayılıyor — adres çubuğuna elle yazılan değer
       ham anahtar basmasın. */
    const googleError = params.get('error');
    if (googleError?.startsWith(GOOGLE_LOGIN_ERROR_PARAM_PREFIX)) {
      const code = googleError.slice(GOOGLE_LOGIN_ERROR_PARAM_PREFIX.length);
      const known = (Object.values(GOOGLE_LOGIN_ERROR) as string[]).includes(code);
      return t(`googleError.${known ? code : GOOGLE_LOGIN_ERROR.FAILED}`);
    }
    return undefined;
  });

  /* Bağlantı sayfası "süresi doldu" derken adresi geri taşıyor (`?email=`):
     kişi aynı adresi ikinci kez yazmasın. Efektte, başlatıcıda değil: sunucu
     boş alan bastı, hidrasyon da boş alanla eşleşmeli. */
  useEffect(() => {
    const value = new URLSearchParams(window.location.search).get('email');
    if (value && EMAIL.test(value)) setEmail(value);
  }, []);

  /** Sunucuya giden adres — `next` başka cihazda açılan bağlantıda da korunsun. */
  async function start(address: string) {
    const result = await startEmailSignIn({
      email: address,
      locale: toLocale(locale),
      next: returnPathFromLocation() ?? undefined,
    });
    if (!result.sent) throw new Error('not-sent');
    return result;
  }

  function onChange(value: string) {
    setEmail(value);
    if (tried) setError(EMAIL.test(value.trim()) ? undefined : t('email.error'));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);

    const address = email.trim().toLowerCase();
    if (!EMAIL.test(address)) {
      setError(t('email.error'));
      emailRef.current?.focus();
      return;
    }

    setError(undefined);
    setFormError(undefined);
    setPending(true);

    try {
      const result = await start(address);
      setSent({ email: address, resendInSeconds: result.resendInSeconds });
    } catch (err) {
      setFormError(
        err instanceof ApiError && err.status === 429
          ? t('tooMany')
          : err instanceof Error && err.message === 'not-sent'
            ? t('notSent')
            : t('notConnected'),
      );
    } finally {
      setPending(false);
    }
  }

  function onSignedIn(session: EmailSignInResponse) {
    router.push(afterSignInPath(session, returnPathFromLocation()));
  }

  const emailId = `${uid}-email`;

  return (
    <section className="auth-col">
      <motion.div
        className="auth-form-inner"
        variants={sceneContainer}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={sceneItem}>
          <AuthBrand />
        </motion.div>

        <motion.div className="auth-swap" layout="position">
          <AnimatePresence mode="wait">
            {sent === null ? (
              <motion.div
                key="email"
                variants={stepV}
                initial="hidden"
                animate="show"
                exit="exit"
              >
                <motion.div className="auth-head" variants={sceneItem}>
                  <h1 className="auth-title">{t('title')}</h1>
                  <p className="auth-lead">{t('lead')}</p>
                </motion.div>

                <motion.div variants={sceneItem}>
                  <GoogleButton label={t('google')} next={returnPathFromLocation() ?? undefined} />
                </motion.div>

                <motion.div variants={sceneItem}>
                  <AuthDivider label={t('divider')} />
                </motion.div>

                <form className="auth-form" onSubmit={onSubmit} noValidate>
                  <motion.div className="auth-field" variants={sceneItem}>
                    <label className="auth-label" htmlFor={emailId}>
                      {t('email.label')}
                    </label>
                    <AuthField
                      invalid={Boolean(error)}
                    >
                      <input
                        ref={emailRef}
                        id={emailId}
                        className="auth-input"
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        placeholder={t('email.placeholder')}
                        value={email}
                        onChange={(e) => onChange(e.target.value)}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? `${emailId}-error` : undefined}
                      />
                    </AuthField>
                    <FieldError id={`${emailId}-error`} message={error} />
                  </motion.div>

                  <FormAlert message={formError ?? notice} />

                  {/* `fieldV` (y'siz), `sceneItem` değil: `layout` taşıyan öğede
                      y ötelemeli giriş ölçüm anına denk gelirse sıçratıyor. */}
                  <motion.div variants={fieldV} layout="position">
                    <SubmitButton
                      pending={pending}
                      label={t('submit')}
                      pendingLabel={t('submitting')}
                    />
                  </motion.div>
                </form>

                <motion.p className="auth-alt" variants={sceneItem}>
                  {t('noAccount')}{' '}
                  <Link href={siteLinks.register.href} className="auth-alt-link">
                    {t('register')}
                  </Link>
                </motion.p>
              </motion.div>
            ) : (
              <motion.div
                key="code"
                variants={stepV}
                initial="hidden"
                animate="show"
                exit="exit"
              >
                <EmailCodeStep
                  email={sent.email}
                  initialResendSeconds={sent.resendInSeconds}
                  onResend={() => start(sent.email)}
                  onBack={() => {
                    setSent(null);
                    setFormError(undefined);
                  }}
                  onSignedIn={onSignedIn}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.p className="auth-legal" variants={sceneItem} layout="position">
          {t.rich('legal', legalChunks)}
        </motion.p>
      </motion.div>
    </section>
  );
}
